import React, { useState, useEffect } from 'react';
import { FaSlack, FaSpinner, FaCheckCircle, FaTimes } from 'react-icons/fa';
import { slackService } from '../services/slack';
import './SlackAction.css';

export default function SlackAction({ messageContent, onSent, messageId, hasBeenSent, allMessages }) {
  const [showModal, setShowModal] = useState(false);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [sentToSlack, setSentToSlack] = useState(hasBeenSent || false);

  // Find the actual content to share (previous assistant message that doesn't mention Slack)
  const findContentToShare = () => {
    if (!allMessages || allMessages.length === 0) {
      return messageContent;
    }
    
    // Look for the most recent assistant message that doesn't mention Slack or sharing instructions
    for (let i = allMessages.length - 1; i >= 0; i--) {
      const msg = allMessages[i];
      if (msg.type === 'assistant' && msg.content) {
        const lowerContent = msg.content.toLowerCase();
        // Skip messages that are about sharing to Slack or confirmation messages
        if (lowerContent.includes('content has been shared') || 
            lowerContent.includes('to share') && lowerContent.includes('slack') ||
            lowerContent.includes('slack channel selection') ||
            lowerContent.startsWith('content has been shared')) {
          continue;
        }
        // This is likely the actual content to share
        return msg.content;
      }
    }
    
    // Fallback to current message if no suitable content found
    return messageContent;
  };

  // Check if message mentions Slack or if user asked to send to Slack
  const shouldShowSlackButton = () => {
    // Don't show button if message has already been sent
    if (sentToSlack) return false;
    
    if (!messageContent) return false;
    const lowerContent = messageContent.toLowerCase();
    
    // Don't show button on confirmation messages
    if (lowerContent.includes('content has been shared') || 
        lowerContent.startsWith('content has been shared')) {
      return false;
    }
    
    // Only show button if the USER explicitly asked to share to Slack
    // Check previous user messages to see if they asked
    if (!allMessages || allMessages.length === 0) {
      return false;
    }
    
    const userMessages = allMessages.filter(m => m.type === 'user');
    if (userMessages.length === 0) {
      return false;
    }
    
    // Check the most recent user message to see if they explicitly asked to share to Slack
    const lastUserMessage = userMessages[userMessages.length - 1];
    if (!lastUserMessage || !lastUserMessage.content) {
      return false;
    }
    
    const userContent = lastUserMessage.content.toLowerCase();
    const userAskedForSlack = userContent.includes('slack') && 
                             (userContent.includes('share') || 
                              userContent.includes('send') || 
                              userContent.includes('post') ||
                              userContent.includes('slack channel'));
    
    // Only show button if user explicitly asked AND Agent V's response mentions Slack
    if (!userAskedForSlack) {
      return false;
    }
    
    // Also check if Agent V's response mentions Slack in context of sharing
    const slackMentioned = lowerContent.includes('slack');
    if (!slackMentioned) {
      return false;
    }
    
    // Show button only if both conditions are met: user asked AND Agent V responded about Slack
    return true;
  };

  const handleOpenModal = async () => {
    setShowModal(true);
    setLoading(true);
    setError(null);
    
    try {
      const response = await slackService.getChannels();
      if (response.status === 'success' && response.data?.channels) {
        setChannels(response.data.channels);
      } else {
        setError('Failed to load Slack channels');
      }
    } catch (err) {
      console.error('Error loading Slack channels:', err);
      setError('Failed to load Slack channels. Please make sure Slack is connected.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendToSlack = async () => {
    if (!selectedChannel) return;

    setSending(true);
    setError(null);

    // Get the actual content to share (not the "how to share" message)
    const contentToShare = findContentToShare();
    
    if (!contentToShare) {
      setError('No content available to share');
      setSending(false);
      return;
    }

    try {
      const response = await slackService.sendMessage(selectedChannel.id, contentToShare);
      if (response.status === 'success') {
        setSuccess(true);
        setSentToSlack(true); // Mark as sent
        setTimeout(() => {
          setShowModal(false);
          setSuccess(false);
          setSelectedChannel(null);
          if (onSent) {
            onSent(selectedChannel.name);
          }
        }, 1500);
      } else {
        setError('Failed to send message to Slack');
      }
    } catch (err) {
      console.error('Error sending to Slack:', err);
      setError(err.message || 'Failed to send message to Slack');
    } finally {
      setSending(false);
    }
  };

  if (!shouldShowSlackButton()) {
    return null;
  }

  return (
    <>
      <button
        className="slack-action-button"
        onClick={handleOpenModal}
        title="Send to Slack"
      >
        <FaSlack /> Send to Slack
      </button>

      {showModal && (
        <div className="slack-modal-overlay" onClick={() => !sending && setShowModal(false)}>
          <div className="slack-modal" onClick={(e) => e.stopPropagation()}>
            <div className="slack-modal-header">
              <h3>
                <FaSlack /> Send to Slack
              </h3>
              <button
                className="slack-modal-close"
                onClick={() => !sending && setShowModal(false)}
                disabled={sending}
              >
                <FaTimes />
              </button>
            </div>

            <div className="slack-modal-content">
              {loading ? (
                <div className="slack-loading">
                  <FaSpinner className="spinner" />
                  <p>Loading Slack channels...</p>
                </div>
              ) : error && !success ? (
                <div className="slack-error">
                  <p>{error}</p>
                </div>
              ) : success ? (
                <div className="slack-success">
                  <FaCheckCircle />
                  <p>Message sent to #{selectedChannel?.name} successfully!</p>
                </div>
              ) : (
                <>
                  <p className="slack-modal-description">
                    Select a Slack channel to send this message:
                  </p>
                  
                  <div className="slack-channels-list">
                    {channels.length === 0 ? (
                      <p className="slack-no-channels">
                        No channels available. Make sure the Slack bot is added to at least one channel.
                      </p>
                    ) : (
                      channels.map((channel) => (
                        <div
                          key={channel.id}
                          className={`slack-channel-item ${selectedChannel?.id === channel.id ? 'selected' : ''}`}
                          onClick={() => !sending && setSelectedChannel(channel)}
                        >
                          <span className="slack-channel-name">#{channel.name}</span>
                          {channel.isPrivate && (
                            <span className="slack-channel-private">Private</span>
                          )}
                        </div>
                      ))
                    )}
                  </div>

                  {selectedChannel && (
                    <div className="slack-modal-actions">
                      <button
                        className="slack-send-button"
                        onClick={handleSendToSlack}
                        disabled={sending}
                      >
                        {sending ? (
                          <>
                            <FaSpinner className="spinner" /> Sending...
                          </>
                        ) : (
                          <>
                            <FaSlack /> Send to #{selectedChannel.name}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

